// Translation
import { Trans, useTrans } from "@/i18n"

// UI
import { Disclaimer } from "@/components/Disclaimer"
import { CardWrapper } from "@/components/Card"

// Specific UI
import { StepCompletedOverlay } from "./StepCompletedOverlay"

// GraphQL
import {
	RiskKycStateChoices,
	useUserKycStatusQuery,
	KycVerificationDocumentType,
} from "@/api/graphql"

// Env variables
import {
	EXTERNAL_PLATFORM_AFM_LICENSE_URL,
	ZONHUB_SUPPORT_EMAIL,
	EXTERNAL_PLATFORM_MANGOPAY_SHAREHOLDER_DECLARATION_URL,
	EXTERNAL_PLATFORM_KVK_LEI_URL,
	EXTERNAL_PLATFORM_ZONHUB_FAQ_URL,
} from "@/lib/env"

/**
 * BusinessVerificationForm
 * @returns
 */
export function BusinessVerificationForm() {
	const t = useTrans("investor")

	const { data } = useUserKycStatusQuery()

	return (
		<CardWrapper className="relative">
			{/** Show blocking overlay when status is NOT requested */}
			{data?.me?.kyc?.state !== undefined &&
			data?.me?.kyc?.state !==
				RiskKycStateChoices.VerificationRequested ? (
				<StepCompletedOverlay
					state={data?.me?.kyc?.state}
					last_verified_on={data?.me?.kyc?.last_verified_on}
					documents={
						data?.me?.kyc?.documents?.filter(
							Boolean,
						) as KycVerificationDocumentType[]
					}
				/>
			) : null}

			<Disclaimer
				className="mb-3"
				title={t("investor:investor.identity.business.title")}
				message={
					<div className="w-full">
						<p className="mb-4">
							<Trans i18nKey="investor:investor.identity.business.intro" />
						</p>
						<p className="mb-4">
							<Trans
								i18nKey="investor:investor.identity.business.legal"
								components={{
									a: (
										<a // eslint-disable-line jsx-a11y/anchor-has-content
											className="font-medium text-secondary-300 hover:text-secondary-700"
											href={
												EXTERNAL_PLATFORM_AFM_LICENSE_URL
											}
											target="_blank"
											rel="noreferrer"
										/>
									),
								}}
							/>
						</p>
						<p className="mb-4">
							<Trans
								i18nKey="investor:investor.identity.business.requirements.intro"
								components={{
									a: (
										<a // eslint-disable-line jsx-a11y/anchor-has-content
											className="font-medium text-secondary-300 hover:text-secondary-700"
											href={`mailto:${ZONHUB_SUPPORT_EMAIL}`}
										/>
									),
								}}
							/>
						</p>
						<ol className="ml-0 list-decimal">
							<li>
								<Trans i18nKey="investor:investor.identity.business.requirements.list.identity" />
							</li>
							<li>
								<Trans i18nKey="investor:investor.identity.business.requirements.list.coc" />
							</li>
							<li>
								<Trans
									i18nKey="investor:investor.identity.business.requirements.list.ubo"
									components={{
										a: (
											<a // eslint-disable-line jsx-a11y/anchor-has-content
												className="font-medium text-secondary-300 hover:text-secondary-700"
												href={
													EXTERNAL_PLATFORM_MANGOPAY_SHAREHOLDER_DECLARATION_URL
												}
												target="_blank"
												rel="noreferrer"
											/>
										),
									}}
								/>
							</li>
						</ol>
						<p className="mb-4">
							<Trans i18nKey="investor:investor.identity.business.requirements.extra" />
						</p>
						<ol className="mb-4 ml-0 list-decimal" start={4}>
							<li>
								<Trans i18nKey="investor:investor.identity.business.requirements.list.articles" />
							</li>
							<li>
								<Trans
									i18nKey="investor:investor.identity.business.requirements.list.lei"
									components={{
										a: (
											<a // eslint-disable-line jsx-a11y/anchor-has-content
												className="font-medium text-secondary-300 hover:text-secondary-700"
												href={
													EXTERNAL_PLATFORM_KVK_LEI_URL
												}
												target="_blank"
												rel="noreferrer"
											/>
										),
									}}
								/>
							</li>
						</ol>
						<p className="mb-4">
							<Trans i18nKey="investor:investor.identity.business.note" />
						</p>
						<p className="mb-4">
							<Trans i18nKey="investor:investor.identity.business.contact" />
						</p>
						<p className="mb-4">
							<Trans
								i18nKey="investor:investor.identity.business.help"
								components={{
									mail: (
										<a // eslint-disable-line jsx-a11y/anchor-has-content
											className="font-medium text-secondary-300 hover:text-secondary-700"
											href={`mailto:${ZONHUB_SUPPORT_EMAIL}`}
											target="_blank"
											rel="noreferrer"
										/>
									),
									faq: (
										<a // eslint-disable-line jsx-a11y/anchor-has-content
											className="font-medium text-secondary-300 hover:text-secondary-700"
											href={
												EXTERNAL_PLATFORM_ZONHUB_FAQ_URL
											}
											target="_blank"
											rel="noreferrer"
										/>
									),
								}}
							/>
						</p>
					</div>
				}
			/>
		</CardWrapper>
	)
}
